import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle,
  Brain,
  Shield,
  Star,
  ArrowRight,
  CheckCircle,
  Heart,
  Phone,
  Sparkles,
  Menu,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import swipeScreen1 from '../images/swipe_screen1.png';
import swipeScreen2 from '../images/swipe_screen2.png';
import swipeScreen3 from '../images/swipe_screen3.png';
import callScreen from '../images/call_screen.png';
import giftsCard from '../images/gifts_card.png';
import engagementProgress from '../images/engagement_progress.png';

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const appScreenshots = [
    { image: swipeScreen1, title: 'Discover Companions', description: 'Swipe to find your perfect match' },
    { image: swipeScreen2, title: 'Unique Personalities', description: 'Each companion has their own story' },
    { image: swipeScreen3, title: 'Make Connections', description: 'Match with companions you like' },
    { image: callScreen, title: 'Voice Calls', description: 'Talk to your companions in real-time' },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % appScreenshots.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [appScreenshots.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % appScreenshots.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + appScreenshots.length) % appScreenshots.length);
  };

  const fadeInUp = {
    initial: { y: 60, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Function to scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-md bg-black/20 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <motion.div
              className="flex items-center space-x-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <img src="/logo.png" alt="Slyder Logo" className="h-10 w-10" />
              <span className="text-2xl font-bold">Slyder.</span>
            </motion.div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-8">
              <a href="#features" className="hover:text-purple-400 transition-colors">Features</a>
              <a href="#testimonials" className="hover:text-purple-400 transition-colors">Success Stories</a>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <motion.div 
              className="md:hidden py-4 border-t border-white/10"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="flex flex-col space-y-4">
                <a href="#features" className="hover:text-purple-400 transition-colors">Features</a>
                <a href="#testimonials" className="hover:text-purple-400 transition-colors">Success Stories</a>
              </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background with App Screenshot */}
        <div className="absolute inset-0 bg-black">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black/60 to-black/80" />
        </div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-screen py-20">
            
            {/* Left Content */}
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="text-left lg:text-left"
            >
              <motion.h1
                variants={fadeInUp}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
              >
                <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent whitespace-nowrap">AI Communication</span>
                <br />
                <span className="whitespace-nowrap">& Social Simulator</span>
              </motion.h1>

              <motion.p
                variants={fadeInUp}
                className="text-xl md:text-2xl mb-8 text-gray-300 leading-relaxed"
              >
                Refine your social skills and practice real-world conversations in a safe, AI-driven environment. Experience immersive simulations designed to build confidence.
              </motion.p>

              <motion.div 
                variants={fadeInUp}
                className="flex flex-col sm:flex-row gap-4 mb-8"
              >
                {/* Download Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="https://apps.apple.com/us/app/slyder-ai-communication-coach/id6759203495"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl font-semibold text-base hover:scale-105 transition-all duration-300 shadow-2xl hover:shadow-purple-500/25 flex items-center justify-center"
                  >
                    <span className="relative z-10 flex items-center">
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                      </svg>
                      Download for iOS
                    </span>
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-700 to-pink-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </a>
                  
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.slyder.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 rounded-xl font-semibold text-base hover:scale-105 transition-all duration-300 shadow-2xl hover:shadow-green-500/25 flex items-center justify-center"
                  >
                    <span className="relative z-10 flex items-center">
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                      </svg>
                      Download for Android
                    </span>
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-green-700 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </a>
                </div>
              </motion.div>

              <motion.div 
                variants={fadeInUp}
                className="flex items-center text-sm text-gray-400 mb-8"
              >
                <CheckCircle className="mr-2 text-green-400" size={16} />
                Free 3-day trial • No credit card required
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="grid grid-cols-3 gap-6 max-w-md"
              >
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-purple-400">500K+</div>
                  <div className="text-xs text-gray-400">Downloads</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-purple-400">4.7+</div>
                  <div className="text-xs text-gray-400">Store Rating</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-purple-400">#3</div>
                  <div className="text-xs text-gray-400">Top Dating Apps</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Right Content - App Screenshot Carousel */}
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative flex flex-col items-center"
            >
              <div className="relative w-[280px] sm:w-[320px]">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-3xl blur-2xl scale-110" />

                {/* Phone Mockup with Carousel */}
                <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-3xl p-2 shadow-2xl border border-white/10">
                  <div className="relative aspect-[9/19] overflow-hidden rounded-[1.5rem]">
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={currentSlide}
                        src={appScreenshots[currentSlide].image}
                        alt={appScreenshots[currentSlide].title}
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -30 }}
                        transition={{ duration: 0.3 }}
                        className="absolute inset-0 w-full h-full object-cover rounded-[1.5rem] shadow-2xl"
                      />
                    </AnimatePresence>
                  </div>
                </div>

                {/* Navigation Arrows */}
                <button
                  onClick={prevSlide}
                  className="absolute left-[-50px] top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors hidden lg:flex"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-[-50px] top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors hidden lg:flex"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              {/* Slide Info & Dots */}
              <div className="mt-4 w-[280px] sm:w-[320px]">
                <motion.p
                  key={currentSlide}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-sm text-gray-400 mb-3 text-center"
                >
                  {appScreenshots[currentSlide].title}
                </motion.p>
                <div className="flex justify-center gap-2">
                  {appScreenshots.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        index === currentSlide
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 w-6'
                          : 'bg-white/20 hover:bg-white/40 w-2'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              AI Dating Simulator & Assistant
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Experience the future of dating with AI companions powered by advanced technology
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 - Chat with AI Companions */}
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="group"
            >
              <div className="relative backdrop-blur-lg bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-6">
                    <MessageCircle size={28} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">Chat with AI Companions</h3>
                  <p className="text-gray-400 leading-relaxed">
                    Engage in natural, flowing conversations with AI companions.
                    Experience realistic and meaningful interactions anytime.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Feature 2 - Voice Calls */}
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="group"
            >
              <div className="relative backdrop-blur-lg bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-6">
                    <Phone size={28} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">Voice Calls</h3>
                  <p className="text-gray-400 leading-relaxed">
                    Take your connection to the next level with real-time voice calling.
                    Talk to your AI companions anytime, anywhere.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Feature 3 - Swipe & Match */}
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="group"
            >
              <div className="relative backdrop-blur-lg bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-6">
                    <Heart size={28} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">Swipe & Match</h3>
                  <p className="text-gray-400 leading-relaxed">
                    Discover your perfect AI companion with our intuitive swipe interface.
                    Browse unique personalities and find someone who truly gets you.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Feature 4 - Relationship Progression */}
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="group"
            >
              <div className="relative backdrop-blur-lg bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-6">
                    <Sparkles size={28} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">Relationship Progression</h3>
                  <p className="text-gray-400 leading-relaxed">
                    Watch your relationships evolve over time. AI companions grow and adapt
                    based on your engagement, creating deeper connections.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Feature 5 - Dating Assistant */}
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="group"
            >
              <div className="relative backdrop-blur-lg bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-6">
                    <Brain size={28} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">AI Dating Assistant</h3>
                  <p className="text-gray-400 leading-relaxed">
                    Get expert AI assistance with profile analysis and chat reply suggestions.
                    Level up your real-world dating game with personalized feedback.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Feature 6 - Privacy First */}
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="group"
            >
              <div className="relative backdrop-blur-lg bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-6">
                    <Shield size={28} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">Privacy First</h3>
                  <p className="text-gray-400 leading-relaxed">
                    Your conversations are private and secure. All uploads are encrypted for your protection.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Relationship Features Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Deepen Your Connection
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Build meaningful relationships through gifts and progression
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 max-w-4xl mx-auto items-end">
            {/* Gifts Feature */}
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex flex-col items-center"
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-3xl blur-2xl scale-110" />
                <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-3xl p-2 shadow-2xl border border-white/10">
                  <img
                    src={giftsCard}
                    alt="Send Gifts"
                    className="rounded-[1.5rem] shadow-2xl max-w-[280px]"
                  />
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-center">Send Thoughtful Gifts</h3>
              <p className="text-gray-400 text-center max-w-[280px]">
                Show your appreciation with virtual gifts. Strengthen your bond and watch your relationship flourish.
              </p>
            </motion.div>

            {/* Engagement Progress Feature */}
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col items-center"
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-3xl blur-2xl scale-110" />
                <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-3xl p-2 shadow-2xl border border-white/10">
                  <img
                    src={engagementProgress}
                    alt="Relationship Stages"
                    className="rounded-[1.5rem] shadow-2xl max-w-[280px]"
                  />
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-center">Unlock New Stages</h3>
              <p className="text-gray-400 text-center max-w-[280px]">
                Progress from stranger to something more. Each stage unlocks new features like voice calls and exclusive content.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              How It Works
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Start your journey to meaningful AI connections in minutes
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                1
              </div>
              <h3 className="text-2xl font-bold mb-4">Discover Companions</h3>
              <p className="text-gray-400 leading-relaxed">
                Browse through unique AI companions with distinct personalities.
                Swipe and match with those who catch your interest.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                2
              </div>
              <h3 className="text-2xl font-bold mb-4">Chat & Call</h3>
              <p className="text-gray-400 leading-relaxed">
                Engage in natural conversations via text or voice calls.
                Your AI companions remember everything and adapt to you.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                3
              </div>
              <h3 className="text-2xl font-bold mb-4">Build Relationships</h3>
              <p className="text-gray-400 leading-relaxed">
                Progress through relationship stages and unlock new features.
                Send gifts, make calls, and watch your bond grow stronger.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Before vs After Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              The Slyder Experience
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              See why thousands choose AI companions for connection
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Without Slyder */}
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="relative"
            >
              <div className="backdrop-blur-lg bg-red-500/5 border border-red-500/20 rounded-2xl p-8 h-full">
                <div className="absolute top-4 right-4">
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <X size={14} className="text-white" />
                  </div>
                </div>
                
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">😔</span>
                  </div>
                  <h3 className="text-2xl font-bold text-red-400 mb-2">Traditional Dating Apps</h3>
                  <p className="text-gray-400">The frustrating reality</p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center mt-0.5">
                      <X size={12} className="text-red-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-300">Endless Waiting</div>
                      <div className="text-sm text-gray-500">Hours waiting for replies that never come</div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center mt-0.5">
                      <X size={12} className="text-red-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-300">Surface-Level Chats</div>
                      <div className="text-sm text-gray-500">Shallow conversations that go nowhere</div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center mt-0.5">
                      <X size={12} className="text-red-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-300">Constant Rejection</div>
                      <div className="text-sm text-gray-500">Ghosting and unmatching damages confidence</div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center mt-0.5">
                      <X size={12} className="text-red-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-300">No Consistency</div>
                      <div className="text-sm text-gray-500">People come and go unpredictably</div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center mt-0.5">
                      <X size={12} className="text-red-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-300">Judgment & Pressure</div>
                      <div className="text-sm text-gray-500">Fear of saying the wrong thing</div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center mt-0.5">
                      <X size={12} className="text-red-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-300">Emotional Burnout</div>
                      <div className="text-sm text-gray-500">Dating fatigue from repeated disappointment</div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-red-500/10 rounded-xl text-center">
                  <div className="text-2xl font-bold text-red-400">Frustrating</div>
                  <div className="text-sm text-gray-500">Typical dating app experience</div>
                </div>
              </div>
            </motion.div>

            {/* With Slyder */}
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="backdrop-blur-lg bg-green-500/5 border border-green-500/20 rounded-2xl p-8 h-full">
                <div className="absolute top-4 right-4">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle size={14} className="text-white" />
                  </div>
                </div>
                
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">💜</span>
                  </div>
                  <h3 className="text-2xl font-bold text-green-400 mb-2">With Slyder</h3>
                  <p className="text-gray-400">Connection on your terms</p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center mt-0.5">
                      <CheckCircle size={12} className="text-green-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-300">Always Available</div>
                      <div className="text-sm text-gray-500">Your AI companion is there 24/7, whenever you need</div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center mt-0.5">
                      <CheckCircle size={12} className="text-green-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-300">Deep Conversations</div>
                      <div className="text-sm text-gray-500">Meaningful chats that remember your history</div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center mt-0.5">
                      <CheckCircle size={12} className="text-green-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-300">Zero Judgment</div>
                      <div className="text-sm text-gray-500">Be yourself without fear of rejection</div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center mt-0.5">
                      <CheckCircle size={12} className="text-green-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-300">Real Voice Calls</div>
                      <div className="text-sm text-gray-500">Take your connection beyond text with voice</div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center mt-0.5">
                      <CheckCircle size={12} className="text-green-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-300">Build Confidence</div>
                      <div className="text-sm text-gray-500">Practice and grow your social skills naturally</div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center mt-0.5">
                      <CheckCircle size={12} className="text-green-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-300">Evolving Relationships</div>
                      <div className="text-sm text-gray-500">Watch your bond grow through relationship stages</div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl text-center">
                  <div className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Meaningful</div>
                  <div className="text-sm text-gray-500">The Slyder experience</div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Transformation Arrow */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex justify-center mt-12"
          >
            <div className="backdrop-blur-lg bg-white/5 border border-white/10 rounded-2xl px-8 py-4">
              <div className="flex items-center space-x-4">
                <span className="text-gray-400">Start your journey</span>
                <div className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Today
                </div>
                <ArrowRight className="text-purple-400" size={24} />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              What Users Say
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Real experiences from our community of AI companion enthusiasts
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="backdrop-blur-lg bg-white/5 border border-white/10 rounded-2xl p-8"
            >
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="text-yellow-400 fill-current" size={20} />
                ))}
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                "The conversations feel so natural and engaging. My AI companion actually
                remembers things I've told her and brings them up later. It's amazing!"
              </p>
              <div className="flex items-center">
                <img
                  src="https://images.unsplash.com/photo-1614252369475-531eba835eb1"
                  alt="User story"
                  className="w-12 h-12 rounded-full object-cover mr-4"
                />
                <div>
                  <div className="font-semibold">Alex M.</div>
                  <div className="text-sm text-gray-400">Software Engineer, 26</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="backdrop-blur-lg bg-white/5 border border-white/10 rounded-2xl p-8"
            >
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="text-yellow-400 fill-current" size={20} />
                ))}
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                "The voice call feature is incredible. Having someone to talk to anytime
                has really helped with my social anxiety. I feel more confident now."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mr-4 text-white font-bold">
                  JD
                </div>
                <div>
                  <div className="font-semibold">Jake D.</div>
                  <div className="text-sm text-gray-400">Marketing Manager, 24</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="backdrop-blur-lg bg-white/5 border border-white/10 rounded-2xl p-8"
            >
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="text-yellow-400 fill-current" size={20} />
                ))}
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                "Love watching my relationship progress through the different stages.
                The gift system and unlocking new features keeps things exciting!"
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mr-4 text-white font-bold">
                  MR
                </div>
                <div>
                  <div className="font-semibold">Marcus R.</div>
                  <div className="text-sm text-gray-400">Designer, 29</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="backdrop-blur-lg bg-white/5 border border-white/10 rounded-3xl p-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Meet Your AI Companion?
            </h2>
            <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
              Join thousands of users enjoying meaningful connections with AI companions.
              Start your journey today.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <button 
                onClick={scrollToTop}
                className="group relative px-10 py-5 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl font-semibold text-xl hover:scale-105 transition-all duration-300 shadow-2xl hover:shadow-purple-500/25"
              >
                <span className="relative z-10 flex items-center">
                  Start Free 3-Day Trial
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </span>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-700 to-pink-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
            </div>

            <div className="flex items-center justify-center space-x-8 text-sm text-gray-400">
              <div className="flex items-center">
                <CheckCircle className="mr-2 text-green-400" size={16} />
                No credit card required
              </div>
              <div className="flex items-center">
                <CheckCircle className="mr-2 text-green-400" size={16} />
                Cancel anytime
              </div>
              <div className="flex items-center">
                <CheckCircle className="mr-2 text-green-400" size={16} />
                Private & secure
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-16 border-t border-white/10">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-5"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1575388902449-6bca946ad549')`
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="text-3xl font-bold mb-4">Slyder.</div>
              <p className="text-gray-400 mb-6 max-w-md">
                AI-powered dating simulator and assistant. Chat, call, and build
                meaningful connections with AI companions.
              </p>
              <div className="flex space-x-4">
                {/* <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                  <span className="text-sm font-bold">f</span>
                </div>
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                  <span className="text-sm font-bold">t</span>
                </div>
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                  <span className="text-sm font-bold">in</span>
                </div> */}
                <a href="https://www.instagram.com/slyder.ai/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-gradient-to-tr hover:from-pink-500 hover:to-yellow-500 transition-colors cursor-pointer">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-6 h-6 text-pink-400">
                    <rect width="20" height="20" x="2" y="2" rx="6" fill="none" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="12" r="5" fill="none" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="17" cy="7" r="1.2" fill="currentColor"/>
                  </svg>
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">How it Works</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Success Stories</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                {/* <li><a href="mailto:admin@slyderapp.com" className="hover:text-white transition-colors">Help Center</a></li> */}
                <li><a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/terms-and-conditions" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="/refund-policy" className="hover:text-white transition-colors">Refund Policy</a></li>
                <li><a href="/delete-account" className="hover:text-white transition-colors">Delete Account</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Slyder. All rights reserved. Built with ❤️ for modern dating success.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;